import React from "react";
import { useNavigate } from "react-router-dom";
import { Boundaries, UseCases } from "@planzone/core";
import { Event } from "@planzone/events";

import { useMessage } from "@components/useMessage";
import { Props } from "./types";

type RedirectableItem<K extends UseCases> = {
  key: K;
  extractTarget: (event: { type: K; data: Boundaries[K]["success"] }) => string;
};

type Redirectable = {
  [K in UseCases]: RedirectableItem<K>;
}[UseCases];

const redirectables: Redirectable[] = [
  {
    key: UseCases.createZone,
    extractTarget: ({ data }) => `/zones/${data.zone.uuid}`,
  },
  {
    key: UseCases.createProject,
    extractTarget: ({ data }) => `/projects/${data.project.uuid}/lists`,
  },
  {
    key: UseCases.useProjectModel,
    extractTarget: ({ data }) => `/projects/${data.project.uuid}/lists`,
  },
  {
    key: UseCases.createProjectModelFromProject,
    extractTarget: ({ data }) => `/projects/${data.projectModel.uuid}/lists`,
  },
  {
    key: UseCases.duplicateProject,
    extractTarget: ({ data }) => `/projects/${data.project.uuid}/lists`,
  },
  {
    key: UseCases.changePlanzone,
    extractTarget: () => "/",
  },
  {
    key: UseCases.logOut,
    extractTarget: () => "/",
  },
];

function eventTypeToUseCase(str: string): UseCases | null {
  return (
    redirectables.find(
      ({ key }) =>
        str === `${key}Requested` ||
        str === `${key}Succeeded` ||
        str === `${key}Failed`,
    )?.key || null
  );
}

function isRequest(eventType: string): boolean {
  return eventType.slice(-"Requested".length) === "Requested";
}

function isSuccess(eventType: string): boolean {
  return eventType.slice(-"Succeeded".length) === "Succeeded";
}

function isFailure(eventType: string): boolean {
  return eventType.slice(-"Failed".length) === "Failed";
}

function Redirect(_: Props) {
  const navigate = useNavigate();
  const ref = React.useRef<UseCases[]>([]);
  const initiateRedirect = (event: Event) => {
    const useCase = eventTypeToUseCase(event.type);

    if (useCase === null) return;

    const index = ref.current.indexOf(useCase);

    ref.current =
      index !== -1
        ? [
            ...ref.current.slice(0, index),
            useCase,
            ...ref.current.slice(index + 1),
          ]
        : [...ref.current, useCase];
  };
  const redirect = (event: Event) => {
    const useCase = eventTypeToUseCase(event.type);

    if (useCase === null) return;

    const index = ref.current.indexOf(useCase);

    if (index === -1) return;

    const redirectable = redirectables.find(
      ({ key }) => key === ref.current[index],
    );

    if (!redirectable) return;

    ref.current = [
      ...ref.current.slice(0, index),
      ...ref.current.slice(index + 1),
    ];
    navigate(redirectable.extractTarget(event as any));
  };
  const failRedirect = (event: Event) => {
    const useCase = eventTypeToUseCase(event.type);

    if (useCase === null) return;
    const index = ref.current.indexOf(useCase);

    if (index === -1) return;

    ref.current = [
      ...ref.current.slice(0, index),
      ...ref.current.slice(index + 1),
    ];
  };

  useMessage((event) => {
    isRequest(event.type)
      ? initiateRedirect(event)
      : isSuccess(event.type)
        ? redirect(event)
        : isFailure(event.type) && failRedirect(event);
  });

  return <React.Fragment />;
}

export { Redirect };
